import { useState, useMemo, useEffect } from "react";
import { Shield, Users } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  Button,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { AccessTemplateDto, UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import { userHoldsFullFixedRole, userHoldsRoleOnTenants } from "@/features/identity-hub/lib/access-scope";
import { filterGrantableByPermissions } from "@/features/identity-hub/lib/grantable-role";
import { useGrantablePermissionTupleKeys } from "@/features/identity-hub/hooks/useGrantableCatalogKeys";
import { OrgFilterCombo } from "./OrgFilterCombo";
import type { OrgPickerItem } from "./OrgPickerCombobox";
import { PREVENT_DISMISS_PROPS } from "./shared/dialog/prevent-dismiss-on-outside";

interface BulkGrantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUserIds: string[];
  accessByUser: Map<string, UserAccessEntry[]>;
  templates: AccessTemplateDto[];
  orgs: OrgPickerItem[];
  onApply: (templateId: string, tenantIds?: string[]) => void;
  isPending: boolean;
}

export function BulkGrantDialog({
  open, onOpenChange, selectedUserIds, accessByUser,
  templates, orgs, onApply, isPending,
}: BulkGrantDialogProps) {
  const { t } = useTranslation();
  const grantableKeys = useGrantablePermissionTupleKeys();
  const grantableTemplates = useMemo(
    () => filterGrantableByPermissions(templates, grantableKeys),
    [templates, grantableKeys],
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);

  const selectedTemplate = useMemo(
    () => grantableTemplates.find((row) => row.id === selectedTemplateId) ?? null,
    [grantableTemplates, selectedTemplateId],
  );
  const isWildcard = selectedTemplate?.scopeMode === "WILDCARD";

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId("");
      setSelectedTenantIds([]);
    }
  }, [open]);

  useEffect(() => {
    setSelectedTenantIds([]);
  }, [selectedTemplateId]);

  const { eligible, skipped } = useMemo(() => {
    if (!selectedTemplate) {
      return { eligible: [] as string[], skipped: 0 };
    }
    if (isWildcard && selectedTenantIds.length === 0) {
      return { eligible: [] as string[], skipped: 0 };
    }
    const eligible: string[] = [];
    let skipped = 0;
    for (const userId of selectedUserIds) {
      const alreadyHolds = isWildcard
        ? userHoldsRoleOnTenants(accessByUser.get(userId), selectedTemplate.id, selectedTenantIds)
        : userHoldsFullFixedRole(accessByUser.get(userId), selectedTemplate);
      if (alreadyHolds) {
        skipped++;
        continue;
      }
      eligible.push(userId);
    }
    return { eligible, skipped };
  }, [selectedUserIds, accessByUser, selectedTemplate, isWildcard, selectedTenantIds]);

  const handleSubmit = () => {
    if (!selectedTemplate || eligible.length === 0) return;
    if (isWildcard) {
      if (selectedTenantIds.length === 0) return;
      onApply(selectedTemplate.id, selectedTenantIds);
      return;
    }
    onApply(selectedTemplate.id);
  };

  const orgCount = isWildcard
    ? selectedTenantIds.length
    : (selectedTemplate?.tenantIds.length ?? 0);
  const canSubmit = eligible.length > 0 && !isPending && (!isWildcard || selectedTenantIds.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" {...PREVENT_DISMISS_PROPS}>
        <DialogHeader>
          <DialogTitle>{t("iam.dashboard.people.assignFixed.title", "Assign Role")}</DialogTitle>
          <DialogDescription>
            {t(
              "iam.dashboard.people.assignFixed.description",
              "Pick a role. Fixed roles apply to every organization they already include. Wildcard roles need organizations you can access.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-1">
          <div className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
              <Shield className="h-3 w-3" /> {t("iam.dashboard.people.assignFixed.roleLabel", "Role")}
            </span>
            {grantableTemplates.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("iam.dashboard.people.assignFixed.empty", "No roles you can grant")}
              </p>
            ) : (
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                aria-label={t("iam.dashboard.people.assignFixed.roleLabel", "Role")}
              >
                <option value="">{t("iam.dashboard.people.assignFixed.rolePlaceholder", "Select role…")}</option>
                {grantableTemplates.map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </select>
            )}
          </div>

          {isWildcard && (
            <OrgFilterCombo
              mode="multi"
              items={orgs}
              value={selectedTenantIds}
              onValueChange={setSelectedTenantIds}
              className="w-full"
              label={t("iam.dashboard.people.assignFixed.orgLabel", "Organizations")}
              ariaLabel={t("iam.dashboard.people.assignFixed.orgAria", "Organizations this role will grant access to")}
            />
          )}

          {selectedTemplate && (!isWildcard || selectedTenantIds.length > 0) && (
            <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              <Users className="h-3.5 w-3.5 shrink-0" />
              <span>
                {t("iam.dashboard.people.assignFixed.summary", {
                  users: eligible.length,
                  total: selectedUserIds.length,
                  orgs: orgCount,
                  defaultValue: "{{users}} of {{total}} users will get this role across {{orgs}} organizations",
                })}
                {skipped > 0 && (
                  <> · {t("iam.dashboard.people.assignFixed.skipped", {
                    count: skipped,
                    defaultValue: "{{count}} already on this role",
                  })}</>
                )}
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            size="sm"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {isPending
              ? t("iam.dashboard.people.assignFixed.pending", "Applying…")
              : eligible.length > 0
                ? t("iam.dashboard.people.assignFixed.submit", {
                    count: eligible.length,
                    defaultValue: "Assign to {{count}} users",
                  })
                : t("iam.dashboard.people.assignFixed.submitIdle", "Assign")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
